import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { usePreloading, useCorporateMode } from '../../contexts/SiteContext';

// Simple utility to wrap components with motion
const createMotionComponent = (Component) => {
  return motion.create(Component);
};

const AnimatedWord = styled(motion.span)`
  display: inline-block;
  margin-right: 0.25em;
`;

const StaggerText = ({ 
  children, 
  variants, 
  containerVariants, 
  className,
  as: Component = motion.div,
  ...props 
}) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { isPreloading } = usePreloading();
  const { isCorporateMode } = useCorporateMode();
  const isStringContent = typeof children === 'string';
  const words = isStringContent ? children.split(' ') : [];

  // Convert the passed component to a motion component (memoized to prevent remounts)
  const MotionComponent = useMemo(() => createMotionComponent(Component), [Component]);

  // Start animation when preloader finishes OR when corporate mode is active
  useEffect(() => {
    if (!isPreloading || isCorporateMode) {
      setShouldAnimate(true);
    }
  }, [isPreloading, isCorporateMode]);

  // Default variants for elegant word animations
  const defaultVariants = {
    hidden: { 
      opacity: 0, 
      y: 20, 
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 60,
        damping: 20,
        duration: 0.8
      }
    }
  };

  const defaultContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.04,
        delayChildren: 0.1
      }
    }
  };

  // Combine defaults with passed props (passed props take priority)
  const finalVariants = { ...defaultVariants, ...variants };
  const finalContainerVariants = { ...defaultContainerVariants, ...containerVariants };
  
  // Determine animation state
  // If whileInView is provided, let framer-motion handle it (don't block with shouldAnimate)
  // Otherwise, use shouldAnimate to control visibility
  const getAnimateState = () => {
    if (props.whileInView) {
      // For whileInView animations, allow them to work regardless of shouldAnimate
      // The whileInView prop will handle triggering
      return undefined; // undefined lets framer-motion use whileInView
    }
    // For non-whileInView animations, use shouldAnimate
    return shouldAnimate ? "visible" : "hidden";
  };

  // If content is a simple string, animate word-by-word. Otherwise, animate as a single block.
  if (isStringContent) {
    return (
      <MotionComponent
        className={className}
        variants={finalContainerVariants}
        initial="hidden"
        animate={getAnimateState()}
        {...props}
      >
        {words.map((word, index) => (
          <AnimatedWord
            key={index}
            variants={finalVariants}
          >
            {word}
          </AnimatedWord>
        ))}
      </MotionComponent>
    );
  }

  return (
    <MotionComponent
      className={className}
      initial="hidden"
      animate={getAnimateState()}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      {...props}
    >
      {children}
    </MotionComponent>
  );
};

export default StaggerText;
